import { Link } from 'react-router-dom'
import { motion } from 'motion/react'
import { ArrowRight, CheckCircle, Phone, Network as NetworkIcon, Wifi, Shield, BarChart3 } from 'lucide-react'
import { SEO } from '@/lib/seo'
import { Button, Badge, AnimatedCard, CardHeader, CardTitle, CardDescription } from '@/components/ui'
import { CTASection } from '@/components/sections'
import { getServiceById } from '@/data/services'
import { companyInfo } from '@/data/company'

export function Network() {
  const service = getServiceById('network')!

  return (
    <>
      <SEO
        title="Network Solutions"
        description="Build a robust, secure, and scalable network infrastructure that powers your digital transformation."
      />

      {/* Hero Section */}
      <section className="section-padding relative overflow-hidden">
        <div className="absolute inset-0 bg-hero-pattern opacity-50" />
        
        <div className="container-custom relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <Badge variant="glow" className="mb-6">Network Solutions</Badge>
              
              <h1 className="text-display-md md:text-display-lg lg:text-display-xl font-heading font-bold text-white mb-6">
                {service.title}
              </h1>

              <p className="text-lg text-slate-300 mb-8">
                {service.longDescription}
              </p>

              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/contact">
                  <Button size="lg" variant="glow" className="group">
                    Get Network Quote
                    <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                  </Button>
                </Link>
                <a href={`tel:${companyInfo.phoneRaw}`}>
                  <Button size="lg" variant="secondary">
                    <Phone className="w-5 h-5" />
                    {companyInfo.phone}
                  </Button>
                </a>
              </div>
            </motion.div>

            {/* Visual */}
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.2, duration: 0.6 }}
              className="hidden lg:block"
            >
              <div className="glass-card p-8 rounded-3xl">
                <div className="grid grid-cols-2 gap-4">
                  {[
                    { icon: NetworkIcon, label: 'LAN/WAN', desc: 'Enterprise networking' },
                    { icon: Wifi, label: 'Wireless', desc: 'High-speed WiFi' },
                    { icon: Shield, label: 'Security', desc: 'Network protection' },
                    { icon: BarChart3, label: 'Monitoring', desc: '24/7 visibility' },
                  ].map((item, index) => (
                    <motion.div
                      key={item.label}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.4 + index * 0.1, duration: 0.5 }}
                      className="glass-card-hover p-6"
                    >
                      <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-accent-500 to-electric-500 flex items-center justify-center mb-4 shadow-glow">
                        <item.icon className="w-6 h-6 text-white" />
                      </div>
                      <h3 className="font-semibold text-white mb-1">{item.label}</h3>
                      <p className="text-sm text-slate-400">{item.desc}</p>
                    </motion.div>
                  ))}
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="section-padding bg-primary-600/30">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <Badge className="mb-4">Features</Badge>
            <h2 className="text-display-sm md:text-display-md font-heading font-bold text-white mb-4">
              Enterprise-Grade Network Infrastructure
            </h2>
            <p className="text-lg text-slate-400 max-w-2xl mx-auto">
              Our network solutions provide the foundation for all your business applications and communications.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-6">
            {service.features.map((feature, index) => (
              <motion.div
                key={feature.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
              >
                <AnimatedCard className="h-full">
                  <CardHeader>
                    <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-accent-500/20 to-electric-500/20 flex items-center justify-center mb-4">
                      <feature.icon className="w-7 h-7 text-accent-400" />
                    </div>
                    <CardTitle>{feature.title}</CardTitle>
                    <CardDescription className="text-base">
                      {feature.description}
                    </CardDescription>
                  </CardHeader>
                </AnimatedCard>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="section-padding">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <Badge className="mb-4">Benefits</Badge>
              <h2 className="text-display-sm md:text-display-md font-heading font-bold text-white mb-6">
                Why Choose Our Network Solutions?
              </h2>
              <p className="text-lg text-slate-400 mb-8">
                Build a network that supports your business today and scales for tomorrow.
              </p>

              <div className="space-y-4">
                {service.benefits.map((benefit, index) => (
                  <motion.div
                    key={benefit}
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5, delay: index * 0.1 }}
                    className="flex items-center gap-4"
                  >
                    <div className="w-8 h-8 rounded-full bg-success-500/20 flex items-center justify-center flex-shrink-0">
                      <CheckCircle className="w-5 h-5 text-success-500" />
                    </div>
                    <span className="text-slate-300">{benefit}</span>
                  </motion.div>
                ))}
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="glass-card p-8 rounded-3xl"
            >
              <h3 className="text-xl font-heading font-semibold text-white mb-6">
                Network Assessment
              </h3>
              <p className="text-slate-400 mb-6">
                Not sure where to start? Our free network assessment identifies gaps and opportunities in your current infrastructure.
              </p>
              <ul className="space-y-3 text-sm text-slate-300">
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-accent-400" />
                  Current infrastructure analysis
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-accent-400" />
                  Performance benchmarking
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-accent-400" />
                  Security vulnerability review
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-accent-400" />
                  Recommendations report
                </li>
              </ul>
              <Link to="/contact" className="block mt-6">
                <Button className="w-full">Request Free Assessment</Button>
              </Link>
            </motion.div>
          </div>
        </div>
      </section>

      <CTASection />
    </>
  )
}


